<template>
  <div id="branch_inventories">
    <div class="container">

      <Tabs>
        <Panel :title="branch.name" v-if="branch">
          <div class="columns">
            <div class="column">
              <div class="subtitle">
                <span>
                  {{$t('branch.inventories')}}
                </span>
                <div class="inventory-buttons">
                  <button @click="addInventory" class="btn btn-primary">{{$t('branch.add_new_inventory')}}</button>
                  <button @click="addInventoryCloneFrom" class="btn btn-primary">{{$t('branch.add_clone_inventory')}}</button>
                </div>
              </div>
              <div class="inventory" v-for="(inventory, index) of branch.inventories" :key="`i${index}`">
                <div class="inventory-left">
                  <span class="name">{{$to(inventory.name)}}</span>
                  <Tag grey v-if="inventory.id">#{{inventory.id}}</Tag>
                  <Tag v-if="!inventory.id">{{$t('branch.new')}}</Tag>
                </div>
                <div class="inventory-right">
                  <button class="btn btn-primary" @click="editInventory(index)" :style="{visibility: inventory.cloneFromId ? 'hidden' : 'visible'}">{{$t('edit')}}</button>
                  <button class="btn btn-primary" @click="removeUnsavedInventory(index)" v-if="!inventory.id">{{$t('remove')}}</button>
                  <button class="btn btn-primary" @click="setInventoryForDeletion(inventory, true)" v-else-if="!inventory.deleted">{{$t('delete')}}</button>
                  <button class="btn btn-danger"  @click="setInventoryForDeletion(inventory, false)" v-else-if="inventory.deleted">{{$t('undo')}} {{$t('delete')}}</button>
                </div>
              </div>
              <div class="empty" v-if="branch.inventories.length === 0">
                {{ $t('branch.no_inventory') }}
              </div>
            </div>
            <div class="column">
              <div class="subtitle">{{$t('branch.overloadableFields')}}</div>
              <TipBox warning no-border>
                {{$t('tips.inventories.company_fields')}}
              </TipBox>
              <div class="group" v-for="field of fields" :key="`field_${field.name}`">
                <input type="checkbox" :id="`f_${field.name}`" :value="field.name" v-model="branch.overloadableFields">
                <label :for="`f_${field.name}`">{{$t(field.trans)}}</label>
              </div>
            </div>
          </div>
        </Panel>
      </Tabs>

      <div class="footer-actions">
        <button class="btn btn-primary" @click="save">{{$t("save")}}</button>
      </div>

    </div>

    <Modal
      name="edit-inventory"
      :title="editedInventory.id ? $t('branch.edit_inventory') + ' (#' + editedInventory.id + ')' : $t('branch.new_inventory')"
      :actions="[
        {name: $t('back'), action: 'close'},
        {name: $t('save'), action: addInventoryCallback, primary: true}
      ]">
      <LocalizedInput open :title="$t('name')" :translate="editedInventory.name" :validate="$validate" errorsKey="editedInventory.name"/>
    </Modal>

    <Modal
      name="clone-from-company"
      :title="$t('branch.add_clone_inventory')"
      :actions="[
        {name: $t('back'), action: 'close'},
        {name: $t('add'), action: addInventoryCloneFromCallback, primary: true}
      ]">
      <div class="group">
        <label>{{$t('branch.from_company')}}</label>
        <select v-model="clonableInventories.selected">
          <option v-for="i of clonableInventories.all" :value="i" :key="`i_${i.id}`">{{i.name}}</option>
        </select>
      </div>
    </Modal>

  </div>
</template>

<script>
  import Datatable from "@/components/Datatable.vue";
  import LocalizedInput from "@/components/tools/LocalizedInput.vue";
  import Tag from "@/components/tools/Tag.vue";
  import { mapActions } from "vuex";
  import Vue from "vue";

  export default {
    name: "BranchInventories",

    components: { Datatable, LocalizedInput, Tag },

    validate: {
      "editedInventory.name.fr": {
        required: true,
        condition: function() {
          return !this.editedInventory.name.en;
        }
      },
      "editedInventory.name.en": {
        required: true,
        condition: function() {
          return !this.editedInventory.name.fr;
        }
      }
    },

    data() {
      return {
        augment: 'backend',
        fields: [
          {name: 'outOfStock', trans: 'items.out_of_stock'},
          {name: 'qtyInStock', trans: 'items.qty_in_stock'},
          {name: 'externalId', trans: 'items.external_id'},
          {name: 'catalogExclusions', trans: 'inventory.catalogs'},
          {name: 'description', trans: 'items.description'},
        ],
        editedIndex: null,
        editedInventory: {
          id: null,
          name: {fr: '', en: ''},
        },
        clonableInventories: {
          all: null,
          selected: null
        },
        branch: {
          id: null,
          name: null,
          overloadableFields: ['outOfStock'],
          inventories: []
        }
      }
    },

    async mounted() {
      let contextBranch = await this.$store.getters.getContextBranch;
      this.setData(contextBranch);
      this.$store.state.pageTitleTranslationKey = "branch.inventories";
      this.$store.state.pageIcon = "/icon/menu.svg";
    },

    methods: {
      ...mapActions(['saveBranch', 'getCompanies']),

      addInventory() {
        this.editedIndex = null;
        this.editedInventory.id = null;
        this.editedInventory.name = {fr: '', en: ''};
        this.$modal.show("edit-inventory", true);
      },

      editInventory(index) {
        this.editedIndex = index;
        this.editedInventory = JSON.parse(JSON.stringify(this.branch.inventories[index]));
        this.$modal.show("edit-inventory", true);
      },

      addInventoryCallback() {
        if (!this.$validate.valid()) {
          toast(this.$t("branch.error.required"), "warning");
          this.$validate.validateAllOnce();
          return;
        }

        this.$modal.show("edit-inventory", false);
        if (this.editedIndex !== null) {
          this.branch.inventories.splice(this.editedIndex, 1, this.editedInventory)
        } else {
          this.branch.inventories.push(this.editedInventory)
        }
      },

      removeUnsavedInventory(index) {
        this.branch.inventories.splice(index, 1);
      },

      setInventoryForDeletion(inventory, setValue = true) {
        let linkedToCompanies = inventory.companies || [];
        if (setValue && linkedToCompanies.length) {
          toast('Inventory is still linked to: ' + linkedToCompanies.map(c => `"${c.name}"`).join(','), 'error')
        } else {
          Vue.set(inventory, 'deleted', setValue);
        }
      },

      async addInventoryCloneFrom() {
        /* lazy loaded clonable inventories */
        if (!this.clonableInventories.all) {
          showSpinner();
          let companies = await this.getCompanies(this.branch.id);
          hideSpinner();

          this.clonableInventories.all = companies.map(c => ({id: c.inventory, name: c.name}));
        }

        this.clonableInventories.selected = null;
        this.$modal.show('clone-from-company', true);
      },

      addInventoryCloneFromCallback() {
        this.$modal.show("clone-from-company", false);

        let selectedInventory = this.clonableInventories.selected;
        if (selectedInventory) {
          this.branch.inventories.push({
            cloneFromId: selectedInventory.id,
            name: {
              fr: `Clone de ${selectedInventory.name}`,
              en: `Clone of ${selectedInventory.name}`
            }
          })
        }
      },

      setData(data) {
        for (let k of Object.keys(this.branch)) {
          this.branch[k] = data[k];
          if (k === 'inventories') {
            this.branch.inventories = data.inventories.map(({id, name, companies}) => ({id, name, companies}));
          }
        }
      },

      async save() {
        this.$store.commit("showSpinner", true);
        let response = await this.saveBranch({branchId: this.branch.id, augment: this.augment, data: this.branch});
        this.$store.commit("showSpinner", false);

        if (response) {
          this.setData(response);
          this.$store.commit("updateContextBranch", response);
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  #branch_inventories {
    display: flex;
  }

  .empty {
    font-style: italic;
    color: #888;
    font-size: 14px;
    text-align: center;
    margin-bottom: 20px;
  }
  .inventory {
    display: flex;
    justify-content: space-between;
    border: 1px solid #e4e4e4;
    padding: 10px;
    border-radius: 4px;
    margin-bottom: 10px;

    .inventory-left, .inventory-right {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    .name {
      font-weight: bold;
      font-size: 18px;
    }

    .inventory-left {
      align-items: flex-start;
    }

    .inventory-right {
      .btn {
        font-size: 14px;
        padding: 5px 10px;

        &:not(:last-child) {
          margin-bottom: 5px;
        }
      }
    }
  }
  .tag {
    font-weight: 500;
    font-size: 14px;
  }
  .inventory-buttons {
    display: flex;
    flex-direction: column;

    button:not(:last-child) {
      margin-bottom: 5px;
    }
  }
  .btn.btn-danger {
    background-color: red;
    color: white;
  }
</style>
